import { BookingMode, Site, Window } from '../../types/Site';

export type SiteStatus =
  | { kind: 'open'; closesAt?: string }
  | { kind: 'closedWithNext'; opensAt: string }
  | { kind: 'closedNoUpcoming' }
  | { kind: 'closedMoreAvailable' }
  | { kind: 'unconfirmed' };

type WindowWithBookingMode = Window & { bookingMode: BookingMode };

function allWindows(site: Site): WindowWithBookingMode[] {
  return (site.events ?? []).flatMap((event) =>
    (event.windows ?? []).map((window) => ({ ...window, bookingMode: event.bookingMode }))
  );
}

// Only imported (partner-directory) sites get the "call ahead, we don't have reliable hours"
// treatment -- freshtrak/plentifulProvider sites are trusted sources, so a site with no events
// from them just has nothing scheduled right now (see computeSiteStatus's closedNoUpcoming
// fallback), not unreliable data.
export function isSiteUnconfirmed(site: Site): boolean {
  return site.source === 'imported' && allWindows(site).length === 0;
}

export function computeSiteStatus(site: Site, now: Date): SiteStatus {
  if (isSiteUnconfirmed(site)) {
    return { kind: 'unconfirmed' };
  }

  const nowMs = now.getTime();
  const windows = allWindows(site);

  const openWindows = windows.filter(
    (w) => new Date(w.opensAt).getTime() <= nowMs && nowMs <= new Date(w.closesAt).getTime()
  );
  if (openWindows.length > 0) {
    // Latest closesAt among overlapping open windows -- e.g. two concurrent walk-in windows
    // 10-12 and 10-16 are both "open now" at 11:00, and the site stays open until the later of
    // the two, not the earlier.
    const latestClosing = openWindows.reduce((latest, w) =>
      new Date(w.closesAt).getTime() > new Date(latest.closesAt).getTime() ? w : latest
    );
    // A specific closing time is only meaningful when every currently-open window shares one
    // booking mode -- otherwise it can name a different event's closing time as if it applied to
    // the service (e.g. walk-in) a neighbor is actually looking at. E.g. a walk-in window open
    // 12-4 and a reservation window open 12-8 are both "open now" at 3:00; claiming "open until
    // 8:00 PM" would send a walk-in-only neighbor to a site that's already closed to them by 4.
    const distinctBookingModes = new Set(openWindows.map((w) => w.bookingMode));
    return distinctBookingModes.size > 1
      ? { kind: 'open' }
      : { kind: 'open', closesAt: latestClosing.closesAt };
  }

  const futureWindows = windows.filter((w) => new Date(w.opensAt).getTime() > nowMs);
  if (futureWindows.length > 0) {
    const soonestOpening = futureWindows.reduce((soonest, w) =>
      new Date(w.opensAt).getTime() < new Date(soonest.opensAt).getTime() ? w : soonest
    );
    return { kind: 'closedWithNext', opensAt: soonestOpening.opensAt };
  }

  // An event can report hasMoreWindows without those windows being in this response (the API
  // paginates windows per event) -- "no upcoming services" would contradict the "+ more times
  // available" chip EventBlock renders for that same event, so surface the uncertain case
  // distinctly instead of claiming there's definitely nothing scheduled.
  const hasMoreWindowsElsewhere = (site.events ?? []).some((event) => event.hasMoreWindows);
  if (hasMoreWindowsElsewhere) {
    return { kind: 'closedMoreAvailable' };
  }

  return { kind: 'closedNoUpcoming' };
}
